"use client";

import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface FixedPriceFieldsProps {
  fixedRentalPrice: string;
  setFixedRentalPrice: (value: string) => void;
  fixedSalePrice: string;
  setFixedSalePrice: (value: string) => void;
  loading: boolean;
  handlePriceInputChange: (setter: React.Dispatch<React.SetStateAction<string>>) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  rentalOptions: Array<{ days: number; price: string }>;
  setRentalOptions: (opts: Array<{ days: number; price: string }>) => void;
}

const FixedPriceFields: React.FC<FixedPriceFieldsProps> = ({
  fixedRentalPrice,
  setFixedRentalPrice,
  fixedSalePrice,
  setFixedSalePrice,
  loading,
  handlePriceInputChange,
  rentalOptions,
  setRentalOptions,
}) => {
  const parseAndFormatCurrencyInput = (value: string): string => {
    const rawValue = String(value || '').replace(/[^0-9]/g, '');
    if (!rawValue) return '';
    const numValue = parseFloat(rawValue);
    return formatCurrency(numValue);
  };

  const handleOptionPriceChange = (index: number, raw: string) => {
    // remove non-digits; negative signs not allowed
    const cleaned = String(raw || '').replace(/[^0-9]/g, '');
    const next = rentalOptions.map((o, i) => i === index ? { ...o, price: cleaned } : o);
    setRentalOptions(next);
  };

  const handleOptionDaysChange = (index: number, days: number) => {
    const normalizedDays = Math.max(1, Math.floor(Number(days) || 1));
    const next = rentalOptions.map((o, i) => i === index ? { ...o, days: normalizedDays } : o);
    setRentalOptions(next);
  };

  const addOption = () => {
    setRentalOptions([...rentalOptions, { days: 1, price: '' }]);
  };

  const removeOption = (index: number) => {
    setRentalOptions(rentalOptions.filter((_, i) => i !== index));
  };

  return (
    <div className="grid grid-cols-1 gap-4">
      <div className="grid gap-2">
        <Label htmlFor="fixed-sale-price">Precio de Venta ({formatCurrency(0)})</Label>
        <Input
          id="fixed-sale-price"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder={formatCurrency(1500)}
          value={parseAndFormatCurrencyInput(fixedSalePrice)}
          onChange={handlePriceInputChange(setFixedSalePrice)}
          disabled={loading}
          className="tabular-nums"
        />
      </div>

      <div className="mt-2">
        <Label className="mb-2">Opciones de Alquiler (precio — días)</Label>
        <div className="space-y-2">
          {rentalOptions.map((opt, idx) => (
            <div key={idx} className="flex items-center space-x-2">
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder={formatCurrency(500)}
                value={parseAndFormatCurrencyInput(opt.price)}
                onChange={(e) => handleOptionPriceChange(idx, e.target.value)}
                disabled={loading}
                className="w-36 tabular-nums"
              />
              <Input
                type="number"
                min={1}
                value={opt.days}
                onChange={(e) => handleOptionDaysChange(idx, Number(e.target.value || 1))}
                disabled={loading}
                className="w-24"
              />
              <Button variant="ghost" onClick={() => removeOption(idx)} disabled={loading}>Eliminar</Button>
            </div>
          ))}
        </div>
        <div className="mt-2">
          <Button onClick={addOption} disabled={loading}>Agregar opción</Button>
        </div>
      </div>
    </div>
  );
};

export default FixedPriceFields;